/**
 * Small, shared vocabulary of the Data Suite's patterns (SDD-024 U-1…U-3): the four shelves, the
 * type badges and how a lake column name is shown.
 */

export type ShelfId = 'filters' | 'rows' | 'columns' | 'values';

/** Present to assistive technology, invisible on screen (the live region, the SR-only table). */
export const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/** The keyboard shortcuts of "Verschieben nach …" (U-2): R/C/V/F. */
export const SHELF_SHORTCUT: Readonly<Record<ShelfId, string>> = {
  rows: 'R',
  columns: 'C',
  values: 'V',
  filters: 'F',
};

export const SHELF_FOR_KEY: Readonly<Record<string, ShelfId>> = {
  r: 'rows',
  c: 'columns',
  v: 'values',
  f: 'filters',
};

/** P-1: rows ≤ 3, columns ≤ 2, values ≤ 5; filters ≤ 20 (the schema's `maxItems`). */
export const SHELF_LIMIT: Readonly<Record<ShelfId, number>> = {
  filters: 20,
  rows: 3,
  columns: 2,
  values: 5,
};

export type FieldKind = 'STRING' | 'NUMBER' | 'DATE' | 'DATETIME' | 'BOOLEAN' | 'CALCULATED';

/** The badge of mockup board 4: str / num / dat / bool / fx. */
export function typeBadge(kind: string): string {
  switch (kind) {
    case 'NUMBER':
      return 'num';
    case 'DATE':
    case 'DATETIME':
      return 'dat';
    case 'BOOLEAN':
      return 'bool';
    case 'CALCULATED':
      return 'fx';
    default:
      return 'str';
  }
}

/**
 * A field as the UI shows it: the lake's lowercase column names get their words capitalised
 * (`produkt name` → `Produkt Name`, as the QuickSight UI shows them); a name that already carries
 * capitals (a custom dataset's alias, a calculated field) or an underscore (`pi_net`, `car_vin`)
 * is shown as it is.
 */
export function displayFieldName(name: string): string {
  if (name !== name.toLowerCase() || name.includes('_')) return name;
  return name
    .split(' ')
    .map((word) => (word === '' ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}
